import { useFlags } from "launchdarkly-react-client-sdk";
import { objectFromEntries } from "@raven/shared/utils";
import { useIsStorybook } from "./storyHooks";
import { getIsCypressRunning } from "constants/constants";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import useQueryString from "./useQueryString";
import { cypressOverridesAtom } from "./syncCypressOverrides";

// LaunchDarkly useFlagsWithDefaults serves as a home for feature flags, i.e. flags that can be toggled on/off for specific customers, pages, urls, emails etc.
// whereas flightConfig serves as home for more long-term-stable configs, LD flags are used for more temporary, experimental, dark-code type features, or features which need to be instantly responsive (flightConfig required a page reload)

/** global state that's first populated with feature flags from LD, which we can then edit in FlagsEditor */
export const editableFeatureFlagsAtom = atom<
  FeatureFlags | Record<string, unknown>
>({});
/** whether feature flags have been fetched from LD */
export const hasLDClientBeenIdentifiedAtom = atom<boolean>(false);

export type FeatureFlags = {
  /** force-trace This flag will force tracing for specific users. There is a performance impact to enabling this flag, so we typically only want to enable it for individual users, not whole groups at a time. */
  forceTrace: boolean;
  /** has-wip-increase Customers who have WIP processes in their lines */
  hasWipIncrease: boolean;
  /** flight-service-outage show the ServiceOutageIndicator */
  flightServiceOutage: boolean;
  /** flight-group-metrics-hide-metrics comma-separated list of metrics to hide in the GroupMetrics component (top of Tiles/Timeline/Hublet pages) - metrics options are: teep, avail, uptime, oee, opeq, usle, netCount, unplannedPercent, operationalAvailability, efficiency, qualityPercent, performance - example string: "teep, oee" */
  flightGroupMetricsHideMetrics: string;
  /** flight-degraded-service-banner-enabled allow displaying the "Degraded Service" banner when a flight servicesStatus breaker is open */
  flightDegradedServiceBannerEnabled: boolean;
  /** flight-force-reload-app-once set to true to force the app to reload once; set back to false before it will work again */
  flightForceReloadAppOnce: boolean;
  /** override whether fetching aggregate data from midgard is enabled (via useIsMidgardDDAEnabled) */
  flightOverrideIsMidgardEnabled: "enabled" | "disabled" | "default";
  /** In left drawer, display hidden link to gpt.raven.ai experiment (default false) */
  flightLinkToGptRavenAi: boolean;
  /** (Devs only) Temporary multipurpose flag for quick reversion of latest test releases (attach this to a recent feature for easy reversion; remove usages after release is stable) */
  flightRevertLatestFeatureTemporaryMultipurpose: boolean;
  /** (temporary) enable magic-string access to all tenants) */
  flightMagicStringEnabled: boolean;
  /** When auth expires, normally we show "Session Expired, Sign In Again" popup, this flag instead auto-kicks user out to login page */
  flightKickOutToLoginOnAuthExpired: boolean;
  /** Allow access to tier0 page via left drawer */
  flightEnableTier0Access: boolean;
  /** Allow access to alternate timelines via HighlightTagsSelector (all raven emails already have access) */
  flightEnableAlternateTimeline: boolean;
  /** Set to a string to automatically clear one item from localStorage e.g. "timeZoneLocaleString" -> clear timezone setting; most Settings are in SettingsManager.tsx */
  flightClearLocalStorageItem: string | null;
  /** Enable refresh tokens in Auth.tsx; this flag's state is stored in localStorage -> since we need to be logged in for this flag to identify the user and become active, we need to stick it in localStorage so that it's still active on the user's next login */
  flightEnableRefreshTokensLs: boolean | null;
  /** Set to a string with the format '<key>:<value>' where <value> is in JSON.stringified format e.g. 'timeZoneLocaleString:"Etc/GMT+6"' or e.g. 'dayStartMsOffset:0' to automatically set one item in localStorage; most Settings are in SettingsManager.tsx */
  flightSetLocalStorageItem: string | null;
  /** Enable access to Snapshots page via the left drawer, which allows downloading snapshots of specified charts */
  flightEnableSnapshotsAccess: boolean;
  /** Enable access to Data Dumps Downloader page via the left drawer, which allows downloading data dumps of specified processes + time ranges */
  flightEnableDataDumpsDownloaderAccess: boolean;
  /** add link in left drawer to analytics page which shows PowerBI embed -- "trial": show the "request a trial" popup; "enabled": link is enabled; "hidden": link is hidden */
  flightLinkToSightAnalytics: "trial" | "enabled" | "hidden";
  /** override any value(s) in flightConfig e.g. { general: { overrideDdaLimitDays: 10 } } */
  flightConfigOverridesFromLd: Record<string, unknown>;
  /** use CheapCanvasProductionSegments to improve Timeline performance */
  flightUseVirtualTimelineSegments: boolean;
  /** show an extra workSchedule tile on WorkScheduleTilesPage that links to the AllWorkSchedules page */
  flightShowAllWorkSchedulesPage: boolean;
  /** custom CSS */
  flightCustomCss: string | null;
  /** access to service-status page via left drawer */
  flightAccessToServiceStatusPage: boolean;
  /** admin mode */
  flightAdminMode: boolean;
};

/** for each flag from LaunchDarkly, set a default value here (required since we use withLDProvider without deferInitialization, so the app loads before LD flags load)*/
// tslint:disable-next-line
export default function useFlagsWithDefaults(): FeatureFlags {
  const flagsFromLD = useFlags() as FeatureFlags;

  const {
    forceTrace,
    hasWipIncrease,
    flightServiceOutage,
    flightGroupMetricsHideMetrics,
    flightDegradedServiceBannerEnabled,
    flightForceReloadAppOnce,
    flightOverrideIsMidgardEnabled,
    flightLinkToGptRavenAi,
    flightRevertLatestFeatureTemporaryMultipurpose,
    flightMagicStringEnabled,
    flightKickOutToLoginOnAuthExpired,
    flightEnableTier0Access,
    flightEnableAlternateTimeline,
    flightClearLocalStorageItem,
    flightEnableRefreshTokensLs,
    flightSetLocalStorageItem,
    flightEnableSnapshotsAccess,
    flightEnableDataDumpsDownloaderAccess,
    flightLinkToSightAnalytics,
    flightConfigOverridesFromLd,
    flightUseVirtualTimelineSegments,
    flightShowAllWorkSchedulesPage,
    flightCustomCss,
    flightAccessToServiceStatusPage,
    flightAdminMode,
  } = flagsFromLD;

  // enable flag overrides in cypress e2e tests via cy.setFeatureFlag(key, val)
  const [cypressOverrides] = useAtom(cypressOverridesAtom);

  const flagsWithDefaults: FeatureFlags = objectFromEntries(
    [
      ["forceTrace", forceTrace, false],
      ["hasWipIncrease", hasWipIncrease, false],
      ["flightServiceOutage", flightServiceOutage, false],
      ["flightGroupMetricsHideMetrics", flightGroupMetricsHideMetrics, ""],
      [
        "flightDegradedServiceBannerEnabled",
        flightDegradedServiceBannerEnabled,
        false,
      ],
      ["flightForceReloadAppOnce", flightForceReloadAppOnce, false],
      [
        "flightOverrideIsMidgardEnabled",
        flightOverrideIsMidgardEnabled,
        // start as disabled if user is trying to disable via url query
        window.location.search.includes("flightOverrideIsMidgardEnabled") &&
        window.location.search.includes("disabled")
          ? "disabled"
          : "default",
      ],
      ["flightLinkToGptRavenAi", flightLinkToGptRavenAi, false],
      [
        "flightRevertLatestFeatureTemporaryMultipurpose",
        flightRevertLatestFeatureTemporaryMultipurpose,
        false,
      ],
      ["flightMagicStringEnabled", flightMagicStringEnabled, false],
      [
        "flightKickOutToLoginOnAuthExpired",
        flightKickOutToLoginOnAuthExpired,
        false,
      ],
      ["flightEnableTier0Access", flightEnableTier0Access, false],
      ["flightEnableAlternateTimeline", flightEnableAlternateTimeline, false],
      ["flightClearLocalStorageItem", flightClearLocalStorageItem, ""],
      ["flightEnableRefreshTokensLs", flightEnableRefreshTokensLs, null],
      ["flightSetLocalStorageItem", flightSetLocalStorageItem, ""],
      ["flightEnableSnapshotsAccess", flightEnableSnapshotsAccess, false],
      [
        "flightEnableDataDumpsDownloaderAccess",
        flightEnableDataDumpsDownloaderAccess,
        false,
      ],
      ["flightLinkToSightAnalytics", flightLinkToSightAnalytics, "trial"],
      ["flightConfigOverridesFromLd", flightConfigOverridesFromLd, {}],
      [
        "flightUseVirtualTimelineSegments",
        flightUseVirtualTimelineSegments,
        true,
      ],
      ["flightShowAllWorkSchedulesPage", flightShowAllWorkSchedulesPage, false],
      ["flightCustomCss", flightCustomCss, null],
      [
        "flightAccessToServiceStatusPage",
        flightAccessToServiceStatusPage,
        false,
      ],
      ["flightAdminMode", flightAdminMode, false],
      // set default values for each item
    ].map(([key, val, defaultVal]) => {
      const cypressOverride = cypressOverrides[String(key)];
      const value = cypressOverride ?? (val === undefined ? defaultVal : val);
      return [key, value];
    }) as [keyof FeatureFlags, any][]
  ) as FeatureFlags;
  // leaving this in next release for testing in production

  const [editableFlags] = useEditableFlags();

  const flagsWithDefaultsOrEditableFlags =
    Object.keys(editableFlags).length > 0 && !getIsCypressRunning()
      ? (editableFlags as FeatureFlags)
      : flagsWithDefaults;

  const flagsOverridesFromQuery = useFlagsOverridesFromQuery();

  return { ...flagsWithDefaultsOrEditableFlags, ...flagsOverridesFromQuery };
}

/** ensure the editable feature flags are synced to LaunchDarkly state on mount */
export function useSyncEditableFlagsToLDFlagsOncePerCustomer() {
  const isStorybook = useIsStorybook();
  const flagsFromLD = useFlags();
  const [, setEditableFlags] = useEditableFlags();

  const [hasLDClientBeenIdentified] = useAtom(hasLDClientBeenIdentifiedAtom);
  // once we get them, make editable via FlagsEditor by syncing flags to editableFlags
  const shouldRun =
    !isStorybook &&
    hasLDClientBeenIdentified &&
    Object.keys(flagsFromLD).length > 0;

  const flagsOverridesFromQuery = useFlagsOverridesFromQuery();

  useEffect(() => {
    if (shouldRun) {
      setEditableFlags({ ...flagsFromLD, ...flagsOverridesFromQuery });
    }
  }, [shouldRun, flagsFromLD, setEditableFlags, flagsOverridesFromQuery]);
}

/** allow overriding feature flags from the url with flagsOverrides=[stringified JSON]
 *
 * usage:
 * ```
 * const myFlagsOverrides = { flightAccessToTrendsPage: true }
 * const encodedFlagsOverrides = encodeURIComponent(JSON.stringify(myFlagsOverrides))
 * const url = `myurl.com/something?flagsOverrides=${encodedFlagsOverrides}`
 * ```
 */
function useFlagsOverridesFromQuery() {
  const parsedQuery = useQueryString();
  if (parsedQuery.flagsOverrides) {
    const parsedOverrides = JSON.parse(
      decodeURIComponent(parsedQuery.flagsOverrides as string)
    );
    return parsedOverrides;
  }
}

export function useEditableFlags() {
  return useAtom(editableFeatureFlagsAtom);
}

/** used in FlagsEditor */
export const FEATURE_FLAG_DESCRIPTIONS: Record<keyof FeatureFlags, string> = {
  flightServiceOutage: `Show the ServiceOutageIndicator`,
  flightGroupMetricsHideMetrics: `Comma-separated list of metrics to hide in the GroupMetrics component (top of Tiles/Timeline/Hublet pages) - metrics options are: teep, avail, uptime, oee, opeq, usle, netCount, unplannedPercen`,
  flightDegradedServiceBannerEnabled: `Allow displaying the "Degraded Service" banner when a flight servicesStatus breaker is open`,
  flightForceReloadAppOnce: `Set to true to force the app to reload once; set back to false before it will work again`,
  flightOverrideIsMidgardEnabled: `Override whether fetching aggregate data from midgard is enabled (via useIsMidgardDDAEnabled)`,
  flightLinkToGptRavenAi: `In left drawer, display hidden link to gpt.raven.ai experiment (default false)`,
  forceTrace: `Force tracing for specific users. There is a performance impact to enabling this flag, so we typically only want to enable it for individual users, not whole groups at a time.`,
  hasWipIncrease: `Customers who have WIP processes in their lines`,
  flightMagicStringEnabled: `(temporary)`,
  flightRevertLatestFeatureTemporaryMultipurpose: `(Devs only) Temporary multipurpose flag for quick reversion of latest test releases — attach this to a recent feature for easy reversion; remove usages after release is stable`,
  flightKickOutToLoginOnAuthExpired: `When auth expires, normally we show "Session Expired, Sign In Again" popup, this flag instead auto-kicks user out to login page`,
  flightEnableTier0Access: `Allow access to tier0 page via left drawer`,
  flightEnableAlternateTimeline: `Allow access to alternate timelines via HighlightTagsSelector (all raven emails already have access)`,
  flightClearLocalStorageItem: `Set to a string to automatically clear one item from localStorage e.g. "timeZoneLocaleString" -> clear timezone setting; most Settings are in SettingsManager.tsx - ⚠ set back to "" after you're done, otherwise the setting will be cleared again on every page load`,
  flightEnableRefreshTokensLs: `Enable refresh tokens in Auth.tsx; this flag's state is stored in localStorage -> since we need to be logged in for this flag to identify the user and become active, we need to stick it in localStorage so that it's still active on the user's next login`,
  flightSetLocalStorageItem:
    "flightSetLocalStorageItem: `Set to a string with the format '<key>:<value>' where <value> is in JSON.stringified format e.g. 'timeZoneLocaleString:\"Etc/GMT+6\"' or e.g. 'dayStartMsOffset:0' to automatically set one item in localStorage; most Settings are in SettingsManager.tsx",
  flightEnableSnapshotsAccess: `Enable access to Snapshots page via the left drawer, which allows downloading snapshots of specified charts`,
  flightEnableDataDumpsDownloaderAccess: `Enable access to Data Dumps Downloader page via the left drawer, which allows downloading data dumps of specified processes + time ranges`,
  flightLinkToSightAnalytics: `add link in left drawer to analytics page which shows PowerBI embed`,
  flightConfigOverridesFromLd: `override any value(s) in flightConfig e.g. { general: { overrideDdaLimitDays: 10 } }`,
  flightUseVirtualTimelineSegments: `use CheapCanvasProductionSegments to improve Timeline performance`,
  flightShowAllWorkSchedulesPage: `show an extra workSchedule tile on WorkScheduleTilesPage that links to the AllWorkSchedules page`,
  flightCustomCss: `custom CSS`,
  flightAccessToServiceStatusPage: `access to service-status page via left drawer`,
  flightAdminMode: `admin mode`,
};
